import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '~/lib/utils';
import styles from './OneWorkspaceV2.module.css';

/**
 * Landing v2 workspace bento — Paper "07 One workspace · dark" (Guri, 2026-09-04).
 *
 * Five cards between the features and the storytelling globe, each a working demo rather than a
 * still: the artifact card is a before/after slider (drag to reveal the code behind the page), the
 * model card autoplays a pick (press the pill, walk the list, land on Gemini), the brand kit's quilt
 * arrives tile by tile and its colours answer hover with their hex, the tool rows roll in one by one,
 * and Copy link really copies and says so.
 *
 * The artifact's rendered half stays light on purpose — it is the artifact's own page, not the
 * site's theme — so its colours are literal hexes rather than remapped tokens, as are the provider
 * and tool logos.
 */

const SHARE_URL = 'usedeck.ai/d/seed-round';

/** Live viewport visibility for pausing loops; latch it locally where entrances need to stick. */
function useInView<T extends HTMLElement>(threshold = 0.35) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { threshold });
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReduce(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduce;
}

/* ── provider and tool logos ──────────────────────────────────────────────────────────────── */

function GoogleLogo({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M22.4856 12.206C22.4856 11.3047 22.4125 10.6469 22.2542 9.96484H11.9619V14.033H18.0033C17.8815 15.044 17.2238 16.5665 15.7621 17.5896L15.7416 17.7258L18.9959 20.2468L19.2213 20.2693C21.2919 18.357 22.4856 15.5434 22.4856 12.206Z"
        fill="#4285F4"
      />
      <path
        d="M11.9619 22.924C14.9216 22.924 17.4064 21.9495 19.2213 20.2687L15.7621 17.589C14.8364 18.2346 13.594 18.6852 11.9619 18.6852C9.06301 18.6852 6.60262 16.773 5.72556 14.1299L5.597 14.1408L2.2132 16.7596L2.16895 16.8826C3.9716 20.4635 7.6744 22.924 11.9619 22.924Z"
        fill="#34A853"
      />
      <path
        d="M5.72589 14.1306C5.49447 13.4485 5.36053 12.7176 5.36053 11.9625C5.36053 11.2072 5.49447 10.4765 5.71371 9.79437L5.70758 9.64911L2.28137 6.98828L2.16927 7.0416C1.42631 8.52761 1 10.1963 1 11.9625C1 13.7286 1.42631 15.3972 2.16927 16.8832L5.72589 14.1306Z"
        fill="#FBBC05"
      />
      <path
        d="M11.9619 5.23869C14.0203 5.23869 15.4088 6.12785 16.2006 6.87089L19.2943 3.85018C17.3943 2.08405 14.9216 1 11.9619 1C7.6744 1 3.9716 3.46039 2.16895 7.04135L5.71338 9.79412C6.60262 7.15102 9.06301 5.23869 11.9619 5.23869Z"
        fill="#EB4335"
      />
    </svg>
  );
}

function ClaudeLogo({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden>
      <path
        d="M5.53051 12.3841L8.40432 10.7715L8.45243 10.6309L8.40432 10.5532H8.26382L7.78296 10.5237L6.1408 10.4793L4.71683 10.4201L3.33719 10.3461L2.98953 10.2721L2.66406 9.84308L2.69733 9.62856L2.98953 9.43259L3.4075 9.46957L4.33212 9.5324L5.71912 9.62856L6.72519 9.68774L8.21571 9.84308H8.45243L8.4857 9.74692L8.40432 9.68774L8.34149 9.62856L6.90638 8.65582L5.35297 7.62762L4.53928 7.03581L4.09911 6.7362L3.87723 6.45514L3.78107 5.84119L4.18055 5.40102L4.71683 5.43806L4.85368 5.47504L5.39737 5.89295L6.55872 6.79179L8.07515 7.90868L8.29709 8.09358L8.38583 8.03069L8.39696 7.98635L8.29709 7.81988L7.47227 6.32936L6.59199 4.81293L6.19993 4.18415L6.0964 3.80692C6.05942 3.65152 6.03351 3.52209 6.03351 3.36304L6.48847 2.74539L6.73991 2.66406L7.34655 2.74544L7.60176 2.96733L7.97899 3.82912L8.58928 5.18656L9.53611 7.03216L9.81352 7.57956L9.96144 8.08628L10.017 8.24162H10.1131V8.15282L10.1907 7.11354L10.335 5.83748L10.4756 4.19527L10.5237 3.73302L10.753 3.17819L11.2079 2.87858L11.563 3.04871L11.8552 3.46668L11.8145 3.73661L11.6406 4.86475L11.3004 6.63274L11.0784 7.81629H11.2079L11.3559 7.66831L11.955 6.87311L12.961 5.61554L13.4049 5.11625L13.9227 4.56514L14.2556 4.30256H14.8844L15.3466 4.99053L15.1396 5.70063L14.4923 6.52174L13.956 7.21706L13.1867 8.25269L12.7058 9.08121L12.7502 9.14776L12.8649 9.13669L14.6032 8.76682L15.5427 8.59664L16.6634 8.40432L17.1701 8.64104L17.2256 8.88147L17.0258 9.37341L15.8275 9.66925L14.422 9.95037L12.3286 10.446L12.3027 10.4645L12.3323 10.5015L13.2754 10.5902L13.6786 10.6124H14.6661L16.5043 10.7493L16.9852 11.0674L17.2737 11.4557L17.2256 11.7516L16.4858 12.1289L15.4872 11.8921L13.1571 11.3374L12.3582 11.1376H12.2472V11.2042L12.913 11.8552L14.1335 12.9574L15.6611 14.3777L15.7387 14.729L15.5427 15.0064L15.3355 14.9768L13.9929 13.9671L13.4752 13.5121L12.3027 12.5246H12.2251V12.6282L12.495 13.0239L13.9227 15.1691L13.9967 15.8275L13.8931 16.0421L13.5233 16.1715L13.1164 16.0975L12.2805 14.9251L11.4187 13.6046L10.7234 12.421L10.6383 12.4692L10.2278 16.889L10.0355 17.1146L9.59158 17.2847L9.22177 17.0037L9.02569 16.5487L9.22172 15.6499L9.45844 14.4775L9.65076 13.5454L9.82459 12.3878L9.92817 12.0031L9.92075 11.9772L9.83572 11.9883L8.96285 13.1867L7.63504 14.9805L6.58463 16.1049L6.33313 16.2048L5.89666 15.9791L5.93735 15.576L6.18144 15.2172L7.63498 13.3679L8.51155 12.2213L9.0775 11.5593L9.0738 11.4631H9.04052L5.17908 13.9708L4.49112 14.0595L4.19522 13.7822L4.2322 13.3272L4.37276 13.1793L5.53411 12.3804L5.53051 12.3841Z"
        fill="#D97757"
      />
    </svg>
  );
}

function GrokLogo({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <g transform="translate(-0.259226 0.118812) scale(0.720072)">
        <path
          d="M13.2371 21.0407L24.3186 12.8506C24.8619 12.4491 25.6384 12.6057 25.8973 13.2294C27.2597 16.5185 26.651 20.4712 23.9403 23.1851C21.2297 25.8989 17.4581 26.4941 14.0108 25.1386L10.2449 26.8843C15.6463 30.5806 22.2053 29.6665 26.304 25.5601C29.5551 22.3051 30.562 17.8683 29.6205 13.8673L29.629 13.8758C28.2637 7.99809 29.9647 5.64871 33.449 0.844576C33.5314 0.730667 33.6139 0.616757 33.6964 0.5L29.1113 5.09055V5.07631L13.2343 21.0436"
          fill="#FFFFFF"
        />
        <path
          d="M10.9503 23.0313C7.07343 19.3235 7.74185 13.5853 11.0498 10.2763C13.4959 7.82722 17.5036 6.82767 21.0021 8.2971L24.7595 6.55998C24.0826 6.07017 23.215 5.54334 22.2195 5.17313C17.7198 3.31926 12.3326 4.24192 8.67479 7.90126C5.15635 11.4239 4.0499 16.8403 5.94992 21.4622C7.36924 24.9165 5.04257 27.3598 2.69884 29.826C1.86829 30.7002 1.0349 31.5745 0.36364 32.5L10.9474 23.0341"
          fill="#FFFFFF"
        />
      </g>
    </svg>
  );
}

function OpenAILogo({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M21.2387 9.98625C21.4842 9.24683 21.5691 8.46352 21.4878 7.68865C21.4064 6.91379 21.1606 6.16521 20.7668 5.49292C19.569 3.40791 17.1616 2.33541 14.8103 2.83938C14.1593 2.11523 13.3292 1.57513 12.4034 1.27331C11.4776 0.971487 10.4887 0.91858 9.53599 1.1199C8.58327 1.32122 7.70029 1.76967 6.97573 2.42023C6.25117 3.07078 5.71054 3.90053 5.40814 4.82615C4.64472 4.98269 3.9235 5.30038 3.29269 5.75799C2.66188 6.21559 2.136 6.80257 1.75022 7.47969C0.539371 9.56139 0.814286 12.1872 2.42993 13.9731C2.18348 14.7121 2.0977 15.4953 2.17833 16.2702C2.25895 17.0451 2.50412 17.7938 2.89743 18.4663C4.09677 20.5521 6.50577 21.6245 8.85825 21.1199C9.37547 21.7023 10.011 22.1677 10.7224 22.4849C11.4338 22.8022 12.2047 22.9641 12.9836 22.9597C15.3935 22.9618 17.5286 21.4061 18.2648 19.1113C19.0281 18.9545 19.7492 18.6367 20.3799 18.1791C21.0107 17.7216 21.5366 17.1347 21.9226 16.4578C23.1188 14.3798 22.8428 11.7682 21.2387 9.98625ZM12.9836 21.5225C12.0216 21.5241 11.0899 21.1869 10.3516 20.5702L10.4815 20.4966L14.8536 17.9729C14.9624 17.9091 15.0528 17.8181 15.1158 17.7088C15.1788 17.5995 15.2123 17.4757 15.2129 17.3496V11.1853L17.0614 12.2545C17.0798 12.2639 17.0927 12.2816 17.096 12.3022V17.4101C17.0912 19.6794 15.2528 21.5178 12.9836 21.5225ZM4.14416 17.7478C3.66174 16.9149 3.48853 15.9384 3.65499 14.9903L3.78479 15.0682L8.16127 17.592C8.26956 17.6555 8.39284 17.689 8.51839 17.689C8.64394 17.689 8.76722 17.6555 8.87551 17.592L14.2216 14.5099V16.644C14.2211 16.655 14.2181 16.6658 14.2129 16.6755C14.2076 16.6853 14.2003 16.6937 14.1913 16.7002L9.76296 19.2543C7.79506 20.3879 5.28088 19.7138 4.14416 17.7478ZM2.99262 8.2243C3.47839 7.38595 4.24513 6.74651 5.15709 6.4192V11.6137C5.15544 11.7392 5.18751 11.8629 5.24994 11.9718C5.31238 12.0807 5.4029 12.1708 5.51205 12.2328L10.8322 15.302L8.98373 16.3712C8.97374 16.3765 8.9626 16.3792 8.95128 16.3792C8.93997 16.3792 8.92883 16.3765 8.91883 16.3712L4.49912 13.8216C2.53511 12.6832 1.86157 10.171 2.99262 8.20272V8.2243ZM18.1782 11.7523L12.8407 8.65288L14.6849 7.58799C14.6949 7.58268 14.706 7.5799 14.7174 7.5799C14.7287 7.5799 14.7398 7.58268 14.7498 7.58799L19.1696 10.142C19.8453 10.5319 20.3962 11.1061 20.758 11.7973C21.1197 12.4886 21.2773 13.2685 21.2124 14.046C21.1475 14.8235 20.8627 15.5665 20.3914 16.1883C19.9201 16.81 19.2816 17.2848 18.5505 17.5573V12.3627C18.5467 12.2374 18.5104 12.1153 18.4451 12.0083C18.3798 11.9013 18.2878 11.8131 18.1782 11.7523ZM20.0179 8.98626L19.888 8.90825L15.5203 6.36293C15.4113 6.29901 15.2873 6.26531 15.161 6.26531C15.0347 6.26531 14.9107 6.29901 14.8017 6.36293L9.45986 9.44513V7.31105C9.45873 7.3002 9.46054 7.28924 9.46511 7.27934C9.46968 7.26944 9.47685 7.26095 9.48584 7.25478L13.9056 4.70506C14.5829 4.31484 15.3574 4.12555 16.1384 4.15934C16.9194 4.19312 17.6747 4.44858 18.3158 4.89584C18.957 5.3431 19.4575 5.96366 19.7589 6.68496C20.0603 7.40625 20.1501 8.19846 20.0178 8.96892V8.98626H20.0179ZM8.45133 12.7696L6.60291 11.7047C6.59367 11.6991 6.58577 11.6916 6.57978 11.6826C6.57378 11.6736 6.56984 11.6634 6.56822 11.6527V6.5578C6.56924 5.7762 6.79275 5.01104 7.21262 4.35178C7.63248 3.69253 8.23134 3.16642 8.93919 2.83497C9.64703 2.50352 10.4346 2.38043 11.2098 2.4801C11.9851 2.57976 12.7159 2.89805 13.3169 3.39776L13.187 3.47137L8.81493 5.99503C8.70612 6.05885 8.61579 6.14988 8.5528 6.25917C8.48981 6.36845 8.45633 6.49225 8.45565 6.61839L8.45133 12.7697V12.7696ZM9.45555 10.6051L11.8365 9.23292L14.2216 10.6052V13.3496L11.8451 14.7219L9.45995 13.3496L9.45555 10.6051Z"
        fill="#FFFFFF"
      />
    </svg>
  );
}

function FigmaLogo() {
  return (
    <svg width="11" height="16" viewBox="0 0 11 16" aria-hidden>
      <path d="M2.75 16C4.28 16 5.5 14.78 5.5 13.25V10.75H2.75C1.22 10.75 0 11.97 0 13.5C0 15.03 1.22 16 2.75 16Z" fill="#0ACF83" />
      <path d="M0 8C0 6.47 1.22 5.25 2.75 5.25H5.5V10.75H2.75C1.22 10.75 0 9.53 0 8Z" fill="#A259FF" />
      <path d="M0 2.75C0 1.22 1.22 0 2.75 0H5.5V5.25H2.75C1.22 5.25 0 4.03 0 2.75Z" fill="#F24E1E" />
      <path d="M5.5 0H8.25C9.78 0 11 1.22 11 2.75C11 4.28 9.78 5.5 8.25 5.5H5.5V0Z" fill="#FF7262" />
      <path d="M11 8C11 9.53 9.78 10.75 8.25 10.75C6.72 10.75 5.5 9.53 5.5 8C5.5 6.47 6.72 5.25 8.25 5.25C9.78 5.25 11 6.47 11 8Z" fill="#1ABCFE" />
    </svg>
  );
}

function LinearLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 100 100" aria-hidden>
      <path
        fill="#5E6AD2"
        d="M1.22541 61.5228c-.2225-.9485.90748-1.5459 1.59638-.857L39.3342 97.1782c.6889.6889.0915 1.8189-.857 1.5964C20.0515 94.4522 5.54779 79.9485 1.22541 61.5228ZM.00189135 46.8891c-.01764375.2833.08887215.5599.28957165.7606L52.3503 99.7085c.2007.2007.4773.3075.7606.2896 2.3692-.1476 4.6938-.46 6.9624-.9259.7645-.157 1.0301-1.0963.4782-1.6481L2.57595 39.4485c-.55186-.5519-1.49117-.2863-1.648174.4782-.465915 2.2686-.77832 4.5932-.92588465 6.9624ZM4.21093 29.7054c-.16649.3738-.08169.8106.20765 1.1l64.77602 64.776c.2894.2894.7262.3742 1.1.2077 1.7861-.7956 3.5171-1.6927 5.1855-2.684.5521-.328.6373-1.0867.1832-1.5407L8.43566 24.3367c-.45409-.4541-1.21271-.3689-1.54074.1832-.99132 1.6684-1.88843 3.3994-2.68399 5.1855ZM12.6587 18.074c-.3701-.3701-.393-.9637-.0443-1.3541C21.7795 6.45931 35.1114 0 49.9519 0 77.5927 0 100 22.4073 100 50.0481c0 14.8405-6.4593 28.1724-16.7199 37.3375-.3903.3487-.984.3258-1.3542-.0443L12.6587 18.074Z"
      />
    </svg>
  );
}

function NotionLogo() {
  return (
    <svg width="15" height="16" viewBox="0 0 15 15.6366" aria-hidden>
      <path
        d="M2.58658 2.74144C3.07295 3.13655 3.2554 3.1064 4.16865 3.04548L12.7785 2.52849C12.9611 2.52849 12.8092 2.34631 12.7484 2.31604L11.3185 1.28233C11.0445 1.06962 10.6794 0.826021 9.97983 0.886955L1.64292 1.49503C1.33887 1.52518 1.27814 1.67719 1.39924 1.79905L2.58658 2.74144ZM3.10352 4.74794V13.807C3.10352 14.2938 3.34682 14.476 3.89442 14.4458L13.3567 13.8983C13.9045 13.8683 13.9655 13.5333 13.9655 13.1378V4.13961C13.9655 3.74475 13.8137 3.5318 13.4783 3.5622L3.59012 4.13961C3.22522 4.17028 3.10352 4.35281 3.10352 4.74794ZM12.4446 5.23389C12.5052 5.50778 12.4446 5.7814 12.1702 5.81217L11.7142 5.90301V12.591C11.3185 12.8038 10.9534 12.9253 10.6493 12.9253C10.1623 12.9253 10.0403 12.7733 9.67558 12.3175L6.69342 7.63584V12.1655L7.63708 12.3784C7.63708 12.3784 7.63708 12.9253 6.87575 12.9253L4.77685 13.0471C4.71587 12.9253 4.77685 12.6216 4.98974 12.5608L5.53745 12.409V6.42001L4.77698 6.35909C4.716 6.08519 4.86788 5.69031 5.29415 5.65966L7.54575 5.50789L10.6493 10.2504V6.05503L9.858 5.9642C9.79725 5.62939 10.0403 5.38628 10.3446 5.35613L12.4446 5.23389ZM0.942925 0.674255L9.61467 0.035663C10.6796 -0.0556787 10.9536 0.00550464 11.6229 0.491713L14.391 2.43728C14.8477 2.77184 15 2.86293 15 3.22764V13.8983C15 14.5671 14.7564 14.9626 13.9047 15.0231L3.83417 15.6313C3.1948 15.6618 2.8905 15.5706 2.55567 15.1448L0.517167 12.4999C0.151892 12.0131 0 11.6488 0 11.2228V1.73788C0 1.19099 0.243683 0.734813 0.942925 0.674255Z"
        fill="#FFFFFF"
      />
    </svg>
  );
}

function GlobeLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
      <circle cx="12" cy="12" r="9" fill="none" stroke="#5EA3E8" strokeWidth="1.8" />
      <ellipse cx="12" cy="12" rx="4" ry="9" fill="none" stroke="#5EA3E8" strokeWidth="1.8" />
      <path d="M3.6 9h16.8M3.6 15h16.8" fill="none" stroke="#5EA3E8" strokeWidth="1.8" />
    </svg>
  );
}

/* ── card 1: before/after — drag to reveal the code behind the page ───────────────────────── */

/**
 * The page behind the slider is a real chart, drawn the way an analytics panel draws one (Beka,
 * 2026-09-15: a better, more realistic chart): monthly logo churn for the year so far, on a labelled
 * axis with gridlines, the release that bent the line marked where it shipped, and the month being
 * read out under the cursor. The code layer spells out the same numbers, so the two halves of the
 * slider agree.
 *
 * The story is the one the old bars told — churn down 38% since March, 3.4 to 2.1 — now with the
 * reason on the chart: the months before the onboarding release faint, after it bright, and August,
 * the month under the cursor, in the highlight. The colours are the stylesheet's — see `.split`.
 */
const CHURN = [
  { month: 'Jan', value: 3.6 },
  { month: 'Feb', value: 3.5 },
  { month: 'Mar', value: 3.4 },
  { month: 'Apr', value: 3.2 },
  { month: 'May', value: 3.0 },
  { month: 'Jun', value: 2.6 },
  { month: 'Jul', value: 2.3 },
  { month: 'Aug', value: 2.1 },
] as const;

/** The axis tops out at 4%, with a gridline at every point. */
const CHURN_MAX = 4;
const CHURN_TICKS = [4, 3, 2, 1, 0] as const;

/** Onboarding v2 shipped at the start of June: the marker sits on the boundary before that month. */
const LAUNCH_INDEX = 5;

/* ── the seam: the dust field's pixels, as a mask between the page and its code ───────────────── */

/*
 * The split's seam is an animated pixel mask (Beka, 2026-09-15), drawn in the dust field's own cells
 * so the one divide on this card speaks the hero's and the morph loader's language. A progressive
 * blur held the seam for a turn and read as a gradient; a 2px white rule held it before that.
 *
 * `HeroDustField`'s shader is the reference, and these are its numbers: the grain (3px cells), the
 * cell hash, and the breathing — `sin(uT * 0.8 * uMotion + phase)` at `uMotion` 2.15.
 */
const SEAM_CELL = 3;
/** How wide the dissolve runs, centred on the handle: sixteen cells. */
const SEAM_BAND = 48;
/** The dust's breathing rate, in radians a second. */
const SEAM_BREATH = 0.8 * 2.15;
/** How far a cell's breath can move its threshold, at the band's middle; nothing at its edges. */
const SEAM_SWING = 0.9;

/** The seam canvas's largest backing store, either side, in device pixels. See `resize`. */
const SEAM_MAX_BACKING = 2048;

/**
 * The artifact's own size: it is laid out once at 480x270, 16:9, and scaled to whatever width the
 * card gives it (Beka, 2026-09-16), so a wider card shows the same page bigger rather than a different
 * page stretched. The stylesheet holds the same two numbers on `.render` and `.codeLayer`.
 */
const ARTIFACT_W = 480;

const fract = (value: number) => value - Math.floor(value);

/** `HeroDustField`'s `hash`, line for line — a pure function of the cell, so the grid never shimmers on its own. */
function cellHash(x: number, y: number): number {
  let px = fract(x * 123.34);
  let py = fract(y * 456.21);
  const d = px * (px + 45.32) + py * (py + 45.32);
  px += d;
  py += d;
  return fract(px * py);
}

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * One frame's worth of the seam, all in DEVICE pixels — the canvas's own units.
 *
 * Device pixels because the cells scale with the artifact: at a scale that is not whole, a 3px cell
 * drawn in CSS pixels lands between device pixels and its edges go soft. Rounding the cell to whole
 * device pixels keeps every cell a crisp square at any card width.
 */
interface SeamFrame {
  width: number;
  height: number;
  /** One cell: the dust's 3px, times the artifact's scale and the screen's, rounded to whole pixels. */
  cell: number;
  /** The dissolve's width: 48 artifact pixels, at the same two scales. */
  band: number;
  /** The split's x this frame, as the loop has it. */
  seamX: number;
  /** Seconds. */
  time: number;
  codeGround: string;
  pageGround: string;
}

/**
 * One frame of the band.
 *
 * Every cell in it belongs to the code or to the page: the chance it is code falls from 1 at the
 * band's left edge to 0 at its right, and its own breath pushes its threshold either way, most at
 * the middle, so the cells nearest the split keep changing hands. A cell that has CROSSED — code on
 * the page's side, page on the code's — is painted in the ground of the side it now belongs to. A cell
 * that has not is left clear, so the real code and the real chart show through it. The one column
 * the split falls inside is always painted, which is what hides the layers' own hard edge.
 *
 * The cells sit on the frame's grid, not the band's, so moving the split moves the dissolve across
 * a still field of pixels rather than dragging a pattern along with it.
 */
function paintSeam(ctx: CanvasRenderingContext2D, frame: SeamFrame) {
  const { width, height, cell, band, seamX, time, codeGround, pageGround } = frame;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, width, height);

  const half = band / 2;
  const first = Math.max(0, Math.floor((seamX - half) / cell));
  const last = Math.min(Math.ceil(width / cell), Math.ceil((seamX + half) / cell));
  const rows = Math.ceil(height / cell);
  const toCode = new Path2D();
  const toPage = new Path2D();

  for (let cx = first; cx < last; cx += 1) {
    const left = cx * cell;
    const offset = left + cell / 2 - seamX;
    const straddles = left < seamX && left + cell > seamX;
    const chance = 1 - smoothstep(-half, half, offset);
    const swing = SEAM_SWING * 2 * chance * (1 - chance);
    for (let cy = 0; cy < rows; cy += 1) {
      const breath = Math.sin(time * SEAM_BREATH + cellHash(cx + 17.3, cy + 17.3) * Math.PI * 2);
      const isCode = cellHash(cx, cy) < chance + breath * swing;
      const top = cy * cell;
      if (isCode && (offset >= 0 || straddles)) toCode.rect(left, top, cell, cell);
      else if (!isCode && (offset < 0 || straddles)) toPage.rect(left, top, cell, cell);
    }
  }

  ctx.fillStyle = codeGround;
  ctx.fill(toCode);
  ctx.fillStyle = pageGround;
  ctx.fill(toPage);
}

/* ── the split: drag anywhere on it, no handle ───────────────────────────────────────────────── */

/** Where the split rests until someone moves it: most of the page, its code beside it. */
const SPLIT_REST = 40;

/**
 * How a keyboard step glides: it closes 63% of the gap every 100ms, whatever the frame rate.
 * Exponential rather than a spring, so it never overshoots the step and swings back. The pointer does
 * not glide: a press or a drag puts the divide under it at once.
 */
const SPLIT_GLIDE_S = 0.1;

/** One arrow-key press, in percent of the card. */
const SPLIT_KEY_STEP = 6;

/*
 * DRAGGED, WITH NO HANDLE (Beka, 2026-09-16). Press anywhere on the card and drag: the divide jumps
 * to the press, stays under the pointer, and stays where it is let go. That is the drag the card had
 * before, without the grip; the hover-follow that replaced it for a day — the divide easing after
 * the cursor and back to rest on leave — is gone. The `ew-resize` cursor is the only affordance.
 *
 * A finger does not move the divide on touch-down. With `pan-y`, a finger that lands here may be
 * scrolling the page, so the divide waits for a sideways move, which only reaches the card once the
 * browser has decided the swipe is not a scroll.
 *
 * ONE LOOP DRAWS BOTH HALVES OF THE DIVIDE. The pointer and the keys only set a target; each frame
 * moves the split to it — straight there for the pointer, on a glide for a key — writes it to
 * `--split`, which the code layer's clip reads, and paints the pixel seam at that same x. The clip
 * used to glide on a CSS transition while the seam chased it by reading the handle's box, and two
 * clocks can disagree for a frame about where an edge is; one value, set once a frame, cannot. No
 * React state per frame, and `aria-valuenow` is written from the loop as the value crosses each
 * whole number.
 *
 * The loop runs while the card is on screen, because the seam breathes even when nothing moves.
 * Reduced motion has no loop: every change is one still redraw.
 */
function ArtifactSplit() {
  const frameRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const codeRef = useRef<HTMLDivElement>(null);
  const target = useRef(SPLIT_REST);
  /**
   * Set by the pointer, read once by the next frame: the divide goes straight to the target instead of
   * gliding. A flag rather than "while pressed", because a quick click can press and release inside
   * one frame, and the loop would never see the press.
   */
  const jump = useRef(false);
  /** From a press to its release: only then does a pointer move reach the split. */
  const dragging = useRef(false);
  /** Redraws now, for the no-loop case; a no-op while the loop is running, which picks the target up. */
  const nudge = useRef<() => void>(() => {});
  const reduceMotion = usePrefersReducedMotion();

  const aim = useCallback((percent: number) => {
    target.current = Math.min(100, Math.max(0, percent));
    nudge.current();
  }, []);

  const aimAtPointer = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const box = event.currentTarget.getBoundingClientRect();
      jump.current = true;
      aim(((event.clientX - box.left) / box.width) * 100);
    },
    [aim],
  );

  const release = useCallback(() => {
    dragging.current = false;
  }, []);

  useEffect(() => {
    const frame = frameRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!frame || !canvas || !ctx) return;

    let codeGround = '#232325';
    let pageGround = '#2446f2';
    let width = 0;
    let height = 0;
    let cell = SEAM_CELL;
    let band = SEAM_BAND;
    let split = target.current;
    let announced = -1;

    /*
     * Sized off the FRAME, and capped. Sized off itself, the canvas fed its own size back into the
     * observer whenever its stylesheet was not applied (a dev reload landing mid-update did it): with
     * no CSS size, a canvas is as big as its backing store, so each resize doubled it at a 2x screen
     * until the browser ran out of memory. The frame does not grow with the canvas once styled, and
     * the cap stops the loop dead even when it is not.
     *
     * The grounds are re-read here too, off the layers as painted, so a stylesheet that arrives after
     * the first frame — which is a resize — reaches the pixels.
     *
     * And this is where the artifact's scale comes from: the frame is 16:9 of the card's width, and
     * `--artifact-scale` is that width over the 480 the artifact is laid out at. The seam's cell and
     * band follow the same scale, so the pixels grow with the page they dissolve.
     */
    const resize = () => {
      const box = frame.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const scale = box.width / ARTIFACT_W || 1;
      frame.style.setProperty('--artifact-scale', String(scale));
      width = Math.round(Math.min(box.width * dpr, SEAM_MAX_BACKING));
      height = Math.round(Math.min(box.height * dpr, SEAM_MAX_BACKING));
      canvas.width = width;
      canvas.height = height;
      cell = Math.max(1, Math.round(SEAM_CELL * scale * dpr));
      band = SEAM_BAND * scale * dpr;
      if (codeRef.current) codeGround = getComputedStyle(codeRef.current).backgroundColor;
      if (pageRef.current) pageGround = getComputedStyle(pageRef.current).backgroundColor;
    };
    const draw = (time: number) => {
      frame.style.setProperty('--split', `${split}%`);
      const whole = Math.round(split);
      if (whole !== announced) {
        frame.setAttribute('aria-valuenow', String(whole));
        announced = whole;
      }
      paintSeam(ctx, { width, height, cell, band, seamX: (split / 100) * width, time, codeGround, pageGround });
    };

    resize();
    draw(performance.now() / 1000);
    const resizer = new ResizeObserver(() => {
      resize();
      draw(performance.now() / 1000);
    });
    resizer.observe(frame);

    if (reduceMotion) {
      nudge.current = () => {
        split = target.current;
        draw(performance.now() / 1000);
      };
      return () => {
        resizer.disconnect();
        nudge.current = () => {};
      };
    }

    let raf = 0;
    let last = 0;
    const loop = (now: number) => {
      // Capped, so a tab that slept does not come back owing the split one enormous step.
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;
      const gap = target.current - split;
      if (jump.current || Math.abs(gap) < 0.01) split = target.current;
      else split += gap * (1 - Math.exp(-dt / SPLIT_GLIDE_S));
      jump.current = false;
      draw(now / 1000);
      raf = requestAnimationFrame(loop);
    };
    const viewer = new IntersectionObserver(([entry]) => {
      cancelAnimationFrame(raf);
      last = 0;
      if (entry.isIntersecting) raf = requestAnimationFrame(loop);
    });
    viewer.observe(frame);
    return () => {
      resizer.disconnect();
      viewer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [reduceMotion]);

  return (
    <div
      ref={frameRef}
      className={styles.split}
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragging.current = true;
        if (event.pointerType !== 'touch') aimAtPointer(event);
      }}
      onPointerMove={(event) => {
        if (dragging.current) aimAtPointer(event);
      }}
      onPointerUp={release}
      onPointerCancel={release}
      onLostPointerCapture={release}
      role="slider"
      aria-label="Reveal the artifact's code"
      aria-valuenow={SPLIT_REST}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'ArrowLeft') aim(target.current - SPLIT_KEY_STEP);
        if (event.key === 'ArrowRight') aim(target.current + SPLIT_KEY_STEP);
      }}
    >
      <div ref={pageRef} className={styles.render} aria-hidden>
        <div className={styles.renderHead}>
          <div>
            <div className={styles.artTitle}>Churn, explained</div>
            <div className={styles.artSub}>Monthly logo churn · 2026</div>
          </div>
          <div className={styles.kpi}>
            <span className={styles.kpiValue}>2.1%</span>
            <span className={styles.kpiDelta}>−38% since March</span>
          </div>
        </div>
        <div className={styles.chart} style={{ '--slots': CHURN.length } as CSSProperties}>
          <div className={styles.yAxis}>
            {CHURN_TICKS.map((tick) => (
              <span
                key={tick}
                className={styles.yTick}
                style={{ '--tick-at': `${(1 - tick / CHURN_MAX) * 100}%` } as CSSProperties}
              >
                {tick}%
              </span>
            ))}
          </div>
          <div className={styles.plot}>
            <span
              className={styles.launch}
              style={{ '--launch-at': `${(LAUNCH_INDEX / CHURN.length) * 100}%` } as CSSProperties}
            >
              <span className={styles.launchLabel}>Onboarding v2</span>
            </span>
            {CHURN.map((point, i) => {
              const last = i === CHURN.length - 1;
              return (
                <span
                  key={point.month}
                  className={cn(
                    styles.bar,
                    last ? styles.barHighlight : i >= LAUNCH_INDEX ? styles.barAfter : styles.barBefore,
                  )}
                  style={{ '--bar-h': `${(point.value / CHURN_MAX) * 100}%` } as CSSProperties}
                >
                  {last && (
                    <>
                      <span className={styles.tooltip}>
                        <span className={styles.swatch} />
                        {point.month} · {point.value}%
                      </span>
                      <svg className={styles.cursorGlyph} width="17" height="18" viewBox="0 0 17 18">
                        <path
                          d="M2 1.5l12.2 10.6-5.7.6 3.1 4.4-2.6 1.5-2.9-4.7-4.1 3.2z"
                          fill="#1B1B1F"
                          stroke="#FCFBF9"
                          strokeWidth="1.4"
                        />
                      </svg>
                    </>
                  )}
                </span>
              );
            })}
          </div>
          <div className={styles.months}>
            {CHURN.map((point) => (
              <span key={point.month}>{point.month}</span>
            ))}
          </div>
        </div>
      </div>
      <div ref={codeRef} className={styles.codeLayer} aria-hidden>
        <pre className={styles.code}>
          <code className={styles.codeInner}>
            <span className={styles.lnTag}>{'<section>'}</span>
            {'\n  '}
            <span className={styles.lnTag}>{'<h1>'}</span>
            <span className={styles.lnText}>Churn, explained</span>
            <span className={styles.lnTag}>{'</h1>'}</span>
            {'\n  '}
            <span className={styles.lnTag}>{'<p>'}</span>
            <span className={styles.lnText}>−38% since March</span>
            <span className={styles.lnTag}>{'</p>'}</span>
            {'\n  '}
            <span className={styles.lnTag}>{'<BarChart'}</span>
            {'\n    '}
            <span className={styles.lnBase}>{'data={['}</span>
            <span className={styles.lnNum}>3.6, 3.5, 3.4, 3.2,</span>
            {'\n           '}
            <span className={styles.lnNum}>3.0, 2.6, 2.3, 2.1</span>
            <span className={styles.lnBase}>{']}'}</span>
            {'\n    '}
            <span className={styles.lnBase}>from=</span>
            <span className={styles.lnStr}>{'"Jan"'}</span>
            <span className={styles.lnBase}> unit=</span>
            <span className={styles.lnStr}>{'"%"'}</span>
            <span className={styles.lnBase}> max=</span>
            <span className={styles.lnNum}>{'{4}'}</span>
            {'\n    '}
            <span className={styles.lnBase}>{'marker={{ at: '}</span>
            <span className={styles.lnStr}>{'"Jun"'}</span>
            <span className={styles.lnBase}>{', label: '}</span>
            <span className={styles.lnStr}>{'"Onboarding v2"'}</span>
            <span className={styles.lnBase}>{' }}'}</span>
            {'\n    '}
            <span className={styles.lnBase}>highlight=</span>
            <span className={styles.lnStr}>{'"Aug"'}</span>
            {'\n  '}
            <span className={styles.lnTag}>{'/>'}</span>
            {'\n'}
            <span className={styles.lnTag}>{'</section>'}</span>
          </code>
        </pre>
      </div>
      {/* The seam: the dust field's pixels, dissolving the page into its code where the split is. */}
      <div className={styles.seamClip} aria-hidden>
        <canvas ref={canvasRef} className={styles.seamCanvas} />
      </div>
    </div>
  );
}

/* ── card 2: the composer autoplays a model pick ──────────────────────────────────────────── */

/** open, walking highlight (-1 = none), pill pressed, dwell in ms. */
const PICK_STEPS = [
  { open: false, highlight: -1, press: false, dwell: 1400 },
  { open: false, highlight: -1, press: true, dwell: 200 },
  { open: true, highlight: 0, press: false, dwell: 700 },
  { open: true, highlight: 1, press: false, dwell: 520 },
  { open: true, highlight: 2, press: false, dwell: 520 },
  { open: true, highlight: 3, press: false, dwell: 520 },
  { open: true, highlight: 0, press: false, dwell: 900 },
  { open: false, highlight: -1, press: false, dwell: 700 },
] as const;

const MODELS = [
  { name: 'Gemini 3.8 Flash', logo: <GoogleLogo /> },
  { name: 'Opus 5', logo: <ClaudeLogo /> },
  { name: 'Grok 4.6', logo: <GrokLogo /> },
  { name: 'GPT-5.6 Sol', logo: <OpenAILogo /> },
] as const;

function ModelPicker({ play }: { play: boolean }) {
  const [stepIndex, setStepIndex] = useState(0);
  const step = PICK_STEPS[stepIndex];

  useEffect(() => {
    if (!play) return;
    const timer = window.setTimeout(() => setStepIndex((index) => (index + 1) % PICK_STEPS.length), step.dwell);
    return () => window.clearTimeout(timer);
  }, [play, stepIndex, step.dwell]);

  const open = play ? step.open : true;
  const highlight = play ? step.highlight : -1;

  return (
    <div className={styles.composerMock} aria-hidden>
      <div className={styles.composer}>
        <div className={styles.prompt}>Rewrite the ask. Shorter, and lead with the number.</div>
        <div className={styles.composerRow}>
          <span className={cn(styles.modelPill, play && step.press && styles.modelPillPressed)}>
            <GoogleLogo size={13} />
            Gemini 3.8 Flash
            <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden>
              <path d="M6 9.5l6 6 6-6" fill="none" stroke="#FFFFFFB3" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className={styles.send}>
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
              <path d="M12 19V5M5 12l7-7 7 7" fill="none" stroke="#141415" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>
      <div className={cn(styles.modelList, open && styles.modelListOpen)}>
        {MODELS.map((model, i) => (
          <div key={model.name} className={cn(styles.modelRow, highlight === i && styles.modelRowHl, i === 0 && styles.modelRowOn)}>
            <span className={styles.modelName}>
              {model.logo}
              <span className={i === 0 ? styles.modelOn : undefined}>{model.name}</span>
            </span>
            {i === 0 && (
              <svg className={styles.check} width="14" height="14" viewBox="0 0 24 24" aria-hidden>
                <path d="M5 12l5 5 9-10" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── card 3: the kit as a quilt — the palette, the type and the spacing it read ───────────────── */

/*
 * THE KIT, LAID OUT LIKE ONE (Beka, 2026-09-15: a row of six swatches and a line of type was too
 * boring a layout). The product's kit overview is a quilt, and so is this: the palette as a mosaic
 * with its primary taking the top of it, and beside it one tile for the type and one for the spacing
 * — the three things the card's own copy says the kit reads.
 *
 * THE PALETTE IS THE CHURN CHART'S — the artifact in the card above this one. Read down the bento,
 * that is the card's sentence acted out: a brand read into a kit, and the kit on the artifact. Each
 * colour carries the ink its hex is written in, since three are light and two are not.
 */
const KIT_PRIMARY = { role: 'Primary', hex: '#2446F2', ink: '#FFFFFF' };
const KIT_COLORS = [
  { hex: '#D6FF3F', ink: '#1B2600' },
  { hex: '#0C1245', ink: '#FFFFFF' },
  { hex: '#8FA3FF', ink: '#0C1245' },
  { hex: '#FFC46B', ink: '#3A2400' },
] as const;

/** The spacing scale, in px: a 4pt grid. */
const KIT_SPACING = [4, 8, 12, 16, 24] as const;

/** The quilt's three tiles arrive in turn, this far apart. */
const KIT_STAGGER_MS = 80;

const swatchStyle = (hex: string, ink: string) => ({ '--swatch': hex, '--swatch-ink': ink }) as CSSProperties;
const tileDelay = (index: number) => ({ '--tile-delay': `${index * KIT_STAGGER_MS}ms` }) as CSSProperties;

function BrandKit({ seen }: { seen: boolean }) {
  return (
    <div className={cn(styles.kit, seen && styles.kitIn)} aria-hidden>
      <div className={cn(styles.kitTile, styles.kitColors)} style={tileDelay(0)}>
        <span className={styles.kitPrimary} style={swatchStyle(KIT_PRIMARY.hex, KIT_PRIMARY.ink)}>
          <span className={styles.kitRole}>{KIT_PRIMARY.role}</span>
          <span className={styles.kitHex}>{KIT_PRIMARY.hex}</span>
        </span>
        {KIT_COLORS.map((color) => (
          <span key={color.hex} className={styles.kitSwatch} style={swatchStyle(color.hex, color.ink)}>
            <span className={styles.kitHex}>{color.hex}</span>
          </span>
        ))}
      </div>
      <div className={cn(styles.kitTile, styles.kitType)} style={tileDelay(1)}>
        <span className={styles.kitAa}>Aa</span>
        <span className={styles.kitLabel}>Inter Display</span>
      </div>
      <div
        className={cn(styles.kitTile, styles.kitSpace)}
        style={{ ...tileDelay(2), '--kit-accent': KIT_COLORS[0].hex } as CSSProperties}
      >
        <span className={styles.kitLabel}>4pt spacing</span>
        <span className={styles.kitSteps}>
          {KIT_SPACING.map((step) => (
            <i key={step} style={{ '--step': `${step}px` } as CSSProperties} />
          ))}
        </span>
      </div>
    </div>
  );
}

/* ── card 4: the sources roll in one by one ───────────────────────────────────────────────── */

const SOURCES = [
  { name: 'Figma', meta: 'Landing v2 · 3 frames', logo: <FigmaLogo /> },
  { name: 'Linear', meta: 'DECK-412 · in review', logo: <LinearLogo /> },
  { name: 'Notion', meta: 'Q3 plan', logo: <NotionLogo /> },
  { name: 'Web', meta: '4 sources kept', logo: <GlobeLogo /> },
] as const;

function Sources({ seen }: { seen: boolean }) {
  return (
    <div className={cn(styles.sources, seen && styles.sourcesIn)} aria-hidden>
      {SOURCES.map((source, i) => (
        <div key={source.name} className={styles.sourceRow} style={{ transitionDelay: seen ? `${i * 110}ms` : '0ms' }}>
          <span className={styles.sourceLogo}>{source.logo}</span>
          <span className={styles.sourceName}>{source.name}</span>
          <span className={styles.sourceMeta}>{source.meta}</span>
        </div>
      ))}
    </div>
  );
}

/* ── card 5: Copy link copies, and says so where you clicked ──────────────────────────────── */

function ShareRow() {
  const [copied, setCopied] = useState(false);
  const resetRef = useRef<number>();

  useEffect(() => () => window.clearTimeout(resetRef.current), []);

  const copy = () => {
    navigator.clipboard?.writeText(`https://${SHARE_URL}`).catch(() => {});
    setCopied(true);
    window.clearTimeout(resetRef.current);
    resetRef.current = window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className={styles.share}>
      <div className={styles.linkRow}>
        <span className={styles.linkText}>{SHARE_URL}</span>
        <button type="button" className={cn(styles.copyPill, copied && styles.copyPillDone)} onClick={copy}>
          <span className={styles.copyLabel}>
            {copied ? (
              <>
                <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden>
                  <path d="M4 12.5l5.5 5.5L20 6.5" fill="none" stroke="#147A3D" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Copied
              </>
            ) : (
              'Copy link'
            )}
          </span>
        </button>
      </div>
      <div className={styles.chips} aria-hidden>
        <span className={styles.chip}>
          <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden>
            <path d="M6 4l14 8-14 8z" fill="#FFFFFF" />
          </svg>
          Present
        </span>
        <span className={styles.chip}>PDF</span>
        <span className={styles.chip}>PowerPoint</span>
      </div>
    </div>
  );
}

/* ── the section ──────────────────────────────────────────────────────────────────────────── */

interface CardCopy {
  title: string;
  body: string;
}

function CardFoot({ title, body }: CardCopy) {
  return (
    <div className={styles.cardCopy}>
      <h3 className={styles.cardTitle}>{title}</h3>
      <p className={styles.cardBody}>{body}</p>
    </div>
  );
}

export interface OneWorkspaceV2Props {
  id?: string;
  className?: string;
}

export function OneWorkspaceV2({ id, className }: OneWorkspaceV2Props) {
  const reduceMotion = usePrefersReducedMotion();
  const { ref: gridRef, inView } = useInView<HTMLDivElement>(0.2);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (inView) setSeen(true);
  }, [inView]);

  return (
    <section id={id} className={cn(styles.section, className)} data-name="OneWorkspaceV2">
      <div className={styles.container}>
        <div className={styles.heading}>
          <h2 className={styles.title}>Everything in one workspace</h2>
          <p className={styles.intro}>
            The brief, the files, the agent and the artifacts sit in one place. You do not move to a
            second tool to finish the job.
          </p>
        </div>
        <div ref={gridRef} className={styles.grid}>
          <div className={styles.row}>
            <div className={cn(styles.card, styles.cardBig)}>
              <ArtifactSplit />
              <CardFoot
                title="Artifacts are live code"
                body="Every slide, doc and chart is a real webpage, not a picture. Drag across it to see the code it is made of."
              />
            </div>
            <div className={cn(styles.card, styles.cardBig)}>
              <ModelPicker play={inView && !reduceMotion} />
              <CardFoot
                title="Pick the model per message"
                body="Opus for the hard rewrite, a fast model for the quick fix. Change it in the composer, not in settings."
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={cn(styles.card, styles.cardSmall)}>
              <BrandKit seen={seen} />
              <CardFoot
                title="Your brand, already applied"
                body="Paste in your website. Deck reads your fonts, colours and spacing into one kit and uses it everywhere."
              />
            </div>
            <div className={cn(styles.card, styles.cardSmall)}>
              <Sources seen={seen} />
              <CardFoot
                title="Connected to your tools"
                body="Figma, Linear, Notion and the web. The agent pulls what it needs into the brief and keeps each source."
              />
            </div>
            <div className={cn(styles.card, styles.cardSmall)}>
              <ShareRow />
              <CardFoot
                title="Share a link, not a file"
                body="Present live in the browser or send the link. Export a PDF or PowerPoint when someone asks for a file."
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

OneWorkspaceV2.displayName = 'OneWorkspaceV2';
